<template>
  <!-- <div style="position: fixed; top: 0; left: 0; height: 100vh; width: 100vw; z-index: 2000"> -->
  <div class="card card-body mb-4 p-4" v-if="!isLoaded || (isLoaded && graphData.length > 0)">
    <template v-if="isLoaded">
      <h3 class="t-500 mb-3 d-flex justify-content-between">
        Top {{ currentView === 'list' ? 50 : 20 }} co-authoring organisations
        <div class="d-flex">
          <i class="al-icon-network t-xxxlr"
            :class="currentView === 'list' ? 't-secondary cursor-pointer' : 't-blue'"
            v-tooltip="currentView === 'list' ? 'Switch to network view' : ''"
            @click="setTreeView('tree')"
          />
          <div class="vertical-divider mx-2" />
          <i class="al-icon-compare_list t-xxxlr"
            :class="currentView === 'tree' ? 't-secondary cursor-pointer' : 't-blue'"
            v-tooltip="currentView === 'tree' ? 'Switch to list view' : ''"
            @click="setTreeView('list')"
          />
        </div>
      </h3>
      <div class="ratio-3-2">
        <div :id="chartId" class="ratio-content bg-white cursor-pointer"
          :style="`z-index: ${currentView === 'tree' ? 1 : 0}`"
        />
        <div class="ratio-content overflow-auto bg-white"
          :style="`z-index: ${currentView === 'list' ? 1 : 0}`"
        >
          <div v-for="organisation in filteredGraphData"
            :key="organisation.data.name"
            class="mb-2 pr-2"
          >
            <div class="d-flex justify-content-between">
              <span class="t-overflow" :title="organisation.label">{{ organisation.label }}</span>
              <div class="text-nowrap">
                <router-link
                  :to="exploreRoute(organisation.value)"
                  v-tooltip="'Click to see co-authored publications'"
                >{{ organisation.count | formatNumber }} <i class="fa fa-search mr-3" /></router-link>
                <router-link v-if="getOrgRoute(organisation)" :to="getOrgRoute(organisation)"
                  v-tooltip="'Click to see profile'"
                >
                  <i class="width--20px text-center d-inline-block" :class="renderOrganisationCategoryIcon(organisation.type)" />
                </router-link>
                <i v-else class="width--20px text-center t-secondary d-inline-block"
                  :class="renderOrganisationCategoryIcon(organisation.type)"
                />
              </div>
            </div>
            <div class="progress" style="height: 6px; background-color: #f7f7f7">
              <div
                class="progress-bar" role="progressbar"
                :aria-valuenow="Math.ceil(organisation.count * 100 / graphData[0].count)"
                aria-valuemin="0" aria-valuemax="100"
                :style="{
                  backgroundColor: colors[organisation.type],
                  width: `${Math.ceil(organisation.count * 100 / graphData[0].count)}%`
                }"
              />
            </div>
          </div>
        </div>
      </div>
      <ul class="list-inline mb-0 mt-4 t-no-select text-center">
        <!-- <li class="list-inline-item t-600">Hide/show:</li> -->
        <li class="list-inline-item"
          v-for="item in legend"
          :key="item.name"
        >
          <i
            class="fa mr-1 fa-square"
            :style="{
              color: item.color
            }"
          />{{ item.label }}
        </li>
      </ul>
    </template>
    <spinner v-else :containerClass="`width--50px min-height--150px`" />
  </div>
</template>

<script>
import Spinner from '@/components/shared/Spinner'

import * as am5 from '@amcharts/amcharts5'
import am5themesAnimated from '@amcharts/amcharts5/themes/Animated'
import * as am5hierarchy from '@amcharts/amcharts5/hierarchy'
import debounce from '@/mixins/debounce'

import { mapGetters, mapActions } from 'vuex'
import { COLORS } from '@/assets/js/_data'

export default {
  name: 'treeChart',
  components: {
    Spinner
  },
  props: {
    isLoaded: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    parent: {
      type: Object,
      default: () => {}
    },
    graphData: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: ''
    },
    exploreRoute: {
      type: Function,
      default: () => {}
    }
  },
  watch: {
    isLoaded (newVal, oldVal) {
      if (newVal !== oldVal && newVal) {
        this.$nextTick(() => this.parseData())
      }
    }
  },
  data () {
    return {
      chartId: '',
      colors: COLORS,
      legend: [{
        name: 'company',
        label: 'Company',
        color: COLORS.company,
        active: true
      }, {
        name: 'academic',
        label: 'University',
        color: COLORS.academic,
        active: true
      }, {
        name: 'hospital',
        label: 'Hospital',
        color: COLORS.hospital,
        active: true
      }, {
        name: 'government',
        label: 'Government',
        color: COLORS.government,
        active: true
      }, {
        name: 'other',
        label: 'Unknown/other',
        color: COLORS.other,
        active: true
      }, {
        name: 'spinoff',
        label: 'Spinoff',
        color: COLORS.spinoff,
        active: true
      }, {
        name: 'consortium',
        label: 'Consortium',
        color: COLORS.consortium,
        active: true
      }, {
        name: 'health',
        label: 'Health',
        color: COLORS.health,
        active: true
      }, {
        name: 'investor',
        label: 'Investor',
        color: COLORS.investor,
        active: true
      }, {
        name: 'incubator',
        label: 'Incubator',
        color: COLORS.incubator,
        active: true
      }]
    }
  },
  mounted () {
    this.chartId = Math.random().toString(36).slice(2, 7) // Generate a random 5-character id for chart-div-container
    this.$nextTick(() => this.isLoaded && this.parseData())
  },
  beforeDestroy () {
    if (this.root) this.root.dispose()
    window.removeEventListener('resize', this.resize)
  },
  computed: {
    ...mapGetters({
      currentView: 'settings/getTreeView'
    }),
    maxCount () {
      let m = 1
      if (this.isEmpty(this.graphData)) return m
      this.graphData.forEach(c => {
        if (c.count > m) m = c.count
      })
      return m
    },
    filteredGraphData () {
      const activeLegends = this.legend.map(item => {
        if (item.active) return item.name
      })
      return this.graphData.filter(org => {
        if (activeLegends.find(i => i === org.type)) return true
      })
    }
  },
  methods: {
    ...mapActions('settings', [
      'setTreeView'
    ]),
    getRatio (count) {
      return count / this.maxCount * 15
    },
    parseData () {
      if (this.graphData.length === 0) return
      this.root = am5.Root.new(this.chartId)

      this.root.setThemes([
        am5themesAnimated.new(this.root)
      ])

      var container = this.root.container.children.push(
        am5.Container.new(this.root, {
          width: am5.percent(100),
          height: am5.percent(100),
          layout: this.root.verticalLayout
        })
      )

      const series = container.children.push(
        am5hierarchy.ForceDirected.new(this.root, {
          categoryField: 'label',
          childDataField: 'children',
          idField: 'value',
          valueField: 'count',
          minRadius: am5.percent(1.5),
          maxRadius: am5.percent(20),
          centerStrength: 1.5,
          manyBodyStrength: -20,
          velocityDecay: 0.6,
          nodePadding: 5,
          xField: 'x',
          yField: 'y',
          toggleKey: 'disabled',
          initialFrames: 60,
          showOnFrame: 60,
          y: 0
        })
      )

      const colors = this.colors

      // Dynamic colors based on element type
      series.circles.template.setup = function (target) {
        target.events.on('dataitemchanged', function (ev) {
          const color = colors[target.dataItem.dataContext.type] || (target.dataItem.dataContext.root && '#90abb6') || colors['other']
          ev.target.set('fill', color)
        })
      }
      series.outerCircles.template.setup = function (target) {
        target.events.on('dataitemchanged', function (ev) {
          const color = colors[target.dataItem.dataContext.type] || (target.dataItem.dataContext.root && '#90abb6') || colors['other']
          ev.target.set('stroke', color)
        })
      }

      // Set tooltip Text depending on node type
      series.nodes.template.setup = (node) => {
        node.events.on('dataitemchanged', (ev) => {
          let target = ev.target
          if (target.dataItem.dataContext.root) {
            target.set('tooltipText', `{category}`)
          } else {
            target.set('tooltipText', `{category}: {count}`)
          }
        })
      }

      series.labels.template.setAll({
        fontSize: 14,
        oversizedBehavior: 'fit'
      })

      // Disable collapsing/exploding of nodes
      series.nodes.template.setAll({
        toggleKey: 'none',
        cursorOverStyle: 'default',
        draggable: true
      })

      const ctaLabel = container.children.unshift(am5.Label.new(this.root, {
        fontSize: 14,
        textAlign: 'center',
        x: am5.percent(50),
        centerX: am5.percent(50),
        paddingTop: 0,
        paddingBottom: 0,
        isMeasured: false,
        layer: 2
      }))

      ctaLabel.hide()

      // Hover events
      series.nodes.template.events.on('pointerover', ev => {
        const item = ev.target.dataItem
        const context = item.dataContext

        if (context.root) return

        if (context.slug) {
          ctaLabel.set('text', '[#475258]Click to see profile • Double click to see co-authored publications[/]')
        } else {
          ctaLabel.set('text', '[#475258]Double click to see co-authored publications[/]')
        }
        ctaLabel.show()

        series.set('velocityDecay', 1)
        series.links.values.forEach(link => {
          link.set('opacity', 0)
        })

        if (!context.intersections) return

        const links = []
        const counts = []

        series.nodes.values.forEach(node => {
          if (node.dataItem.dataContext.root) return

          const intersection = context.intersections[node.dataItem.dataContext.value]
          if (intersection && node.get('visible')) {
            const l = series.linkDataItems(item, node.dataItem, 0)
            links.push({ link: l, count: intersection })
            counts.push(intersection)
          } else {
            if (node.dataItem.dataContext.value !== item.dataContext.value) {
              node.set('opacity', 0.1)
            }
          }
        })
        const max = Math.max(...counts)
        const min = Math.min(...counts)
        links.forEach((link) => {
          link.link.set('strokeWidth', (link.count / (max - min)) * 8)
          link.link.set('stroke', '#43AEC5')
        })
      })

      series.nodes.template.events.on('pointerout', ev => {
        const item = ev.target.dataItem
        const context = item.dataContext

        ctaLabel.hide()

        if (context.root) return

        if (context.intersections) {
          series.nodes.values.forEach(node => {
            if (node.dataItem.dataContext.root) return
            const intersection = context.intersections[node.dataItem.dataContext.value]
            if (intersection) {
              series.unlinkDataItems(item, node.dataItem)
            } else {
              node.set('opacity', 1)
            }
          })
        }

        series.links.values.forEach(link => {
          link.set('opacity', 1)
        })
      })

      const tooltip = am5.Tooltip.new(this.root, {
        getFillFromSprite: false,
        getStrokeFromSprite: false,
        autoTextColor: false
      })

      tooltip.get('background').setAll({
        fill: am5.color(0xffffff),
        fillOpacity: 0.6
      })

      tooltip.label.setAll({
        fill: am5.color(0x000000),
        fontSize: 14,
        oversizedBehavior: 'wrap',
        maxWidth: 400
      })

      series.set('tooltip', tooltip)

      // Click/double click events
      let timer = null

      series.nodes.template.events.on('click', ev => {
        const isMetaKey = ev && ev.originalEvent.metaKey
        const t = ev.target.dataItem.dataContext
        if (!t.root) {
          if (timer) clearTimeout(timer)
          timer = setTimeout(() => {
            this.clickNode(t, isMetaKey)
            timer = null
          }, 500)
        }
      })

      // Double click event
      series.nodes.template.events.on('dblclick', ev => {
        // If we are not clicking the actual canvas, prevent dblClick event from happening.
        // Happens when "list view" is the main view and user double clicks where a node would be on the "tree view"
        if (ev.originalEvent && ev.originalEvent.target && ev.originalEvent.target.tagName) {
          if (ev.originalEvent.target.tagName.toLowerCase() !== 'canvas') return
        }
        const t = ev.target.dataItem.dataContext
        if (timer) {
          clearTimeout(timer)
          timer = null
        }
        this.dblClickNode(t)
      })

      let children = this.graphData
      children = children.slice(0, 20) // only show first 20 on graph
      // Generate a less big center circle value, based on children
      const count = children.reduce((prevValue, curChild) => {
        return prevValue + curChild.count
      }, 0)

      this.networkData = {
        ...this.parent,
        'root': true,
        'children': children,
        count: count / 6 < 1 ? 2 : count / 8,
        x: am5.percent(50),
        y: am5.percent(52)
      }

      series.data.setAll([this.networkData])

      this.series = series

      window.addEventListener('resize', this.resize)
    },
    // Allows network nodes to adjust position for a short time after resizing the window
    resize: debounce(function () {
      this.series.set('velocityDecay', 0.6)
      setTimeout(() => {
        this.series.set('velocityDecay', 1)
      }, 1000)
    }, 500),
    // Toggle visible items (both network and list view)
    clickLegend (item) {
      item.active = !item.active
      const typeToToggle = item.name
      this.series.nodes.values.forEach(node => {
        const context = node.dataItem.dataContext
        if (context.root) return
        if (context.type === typeToToggle) {
          if (!item.active) {
            node.hide()
          } else {
            node.show()
          }
        }
      })
    },
    getOrgRoute (data) {
      if (data.slug) {
        return this.$router.resolve({
          name: `organisation.show`,
          params: {
            id: data.slug
          }
        }).href
      }
      return null
    },
    clickNode (data, altKey = false) {
      if (!data.root) {
        const route = this.getOrgRoute(data)
        if (!route) return
        if (altKey) {
          window.open(route, '_blank')
          return
        }
        this.$router.push(route)
      }
    },
    dblClickNode (data) {
      if (!data.root) {
        this.$router.push(this.exploreRoute(data.value))
      }
    }
  }
}
</script>
